/**
 * Route guards for role-based access control.
 * Bounces users who lack the correct Firebase Custom Claim.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from './LoadingScreen';

/**
 * ProtectedRoute - requires authentication
 */
export const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  
  if (!currentUser) {
    return <Navigate to="/patient/login" state={{ from: location }} replace />;
  }

  return children;
};

/**
 * RoleRoute - requires specific role
 */
export const RoleRoute = ({ children, allowedRoles }) => {
  const { currentUser, userRole, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  
  if (!currentUser) {
    const defaultLogin = allowedRoles.includes('admin') ? '/admin/login' 
      : allowedRoles.includes('doctor') ? '/doctor/login' 
      : '/patient/login';
    return <Navigate to={defaultLogin} state={{ from: location }} replace />;
  }

  // If role is null (no claims set), redirect to login — registration was incomplete
  if (!userRole) {
    const defaultLogin = allowedRoles.includes('admin') ? '/admin/login' 
      : allowedRoles.includes('doctor') ? '/doctor/login' 
      : '/patient/login';
    return <Navigate to={defaultLogin} state={{ from: location }} replace />;
  }

  if (!allowedRoles.includes(userRole)) {
    // Redirect to the user's correct dashboard
    const dashboardMap = {
      patient: '/patient/dashboard',
      doctor: '/doctor/dashboard',
      admin: '/admin/dashboard',
      pending_doctor: '/doctor/pending',
    };
    return <Navigate to={dashboardMap[userRole] || '/patient/login'} replace />;
  }

  return children;
};

/**
 * PatientRoute - only patients
 */
export const PatientRoute = ({ children }) => (
  <RoleRoute allowedRoles={['patient']}>{children}</RoleRoute>
);

/**
 * DoctorRoute - only verified doctors
 */
export const DoctorRoute = ({ children }) => (
  <RoleRoute allowedRoles={['doctor']}>{children}</RoleRoute>
);

/**
 * AdminRoute - only admins
 */
export const AdminRoute = ({ children }) => (
  <RoleRoute allowedRoles={['admin']}>{children}</RoleRoute>
);

/**
 * PublicOnlyRoute - only redirect when the logged-in user's role MATCHES
 * the expected role for this page. If they are logged in as a DIFFERENT role
 * they can still visit the page (e.g. to sign out and switch accounts).
 *
 * Pass role="patient" | "doctor" | "admin" for login pages.
 * Omit role (or pass role="landing") for the landing page — that always redirects
 * any authenticated user.
 */
export const PublicOnlyRoute = ({ children, role }) => {
  const { currentUser, userRole, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  const dashboardMap = {
    patient: '/patient/dashboard',
    doctor: '/doctor/dashboard',
    admin: '/admin/dashboard',
    pending_doctor: '/doctor/pending',
  };

  if (currentUser && userRole) {
    const roleToPortal = {
      patient: 'patient',
      doctor: 'doctor',
      pending_doctor: 'doctor',
      admin: 'admin',
    };

    const userPortal = roleToPortal[userRole];

    // Landing page: always redirect logged-in users to their dashboard
    if (!role || role === 'landing') {
      const target = dashboardMap[userRole];
      if (target) return <Navigate to={target} replace />;
    }

    // Login page: only redirect if THIS portal belongs to the logged-in user
    if (role && userPortal === role) {
      const target = dashboardMap[userRole];
      if (target) return <Navigate to={target} replace />;
    }

    // Different role visiting another portal's login — let them through
    // (Login.js shows a "switch account" banner)
  }

  return children;
};
